import { z } from "zod";
import { MODULE_KEYS, type ModuleKey } from "@/stores/window-store";

/* ──────────────────────────────────────────────────────────────────
 * Desktop search params — the addressable part of the shell.
 *
 * The desktop shell lives at a single route (/dashboard) with N floating
 * windows on top of it, so "where am I" cannot be expressed as path
 * segments without inventing a route hierarchy that does not exist.
 * Search params carry it instead.
 *
 * The split that matters:
 *   • NAVIGATION state (which window is focused, which tab, which record)
 *     lives HERE, in the URL — so Back, Forward, refresh, and shared links
 *     all work by way of the browser's own history stack rather than an
 *     intercepted Back button.
 *   • WORKSPACE state (window x/y, width/height, z-order, minimized, icon
 *     positions) stays in the persisted window-store. Nobody wants
 *     `?x=340&y=120` in their address bar, and moving a window is not
 *     navigation.
 *
 * Per-module params are NAMESPACED (`crmTab`, not `tab`). Every open window
 * renders at once, so a bare `tab` would be read by all of them and each
 * would try to claim it as its own.
 *
 * Every field is optional and typed as a loose string rather than an enum.
 * A malformed or stale URL must degrade to a sensible default, not fail
 * route validation and blank the screen. Consumers narrow and fall back.
 * ────────────────────────────────────────────────────────────────── */

export const desktopSearchSchema = z.object({
  /** Module key of the focused window. */
  win: z.string().optional(),

  /* Per-module tab state. One `<module>Tab` per tabbed module. */
  pmTab: z.string().optional(),
  crmTab: z.string().optional(),
  peopleTab: z.string().optional(),
  financeTab: z.string().optional(),
  inventoryTab: z.string().optional(),
  retainersTab: z.string().optional(),
  qaTab: z.string().optional(),
  rewardsTab: z.string().optional(),
  settingsTab: z.string().optional(),

  /**
   * Selected record, for modules whose tab bar grows a tab per open record.
   * PM needs no equivalent: it supports several open projects at once, so the
   * record id rides inside the tab id itself (`pmTab=project:<uuid>`).
   */
  retainersId: z.string().optional(),
  qaRun: z.string().optional(),
  /**
   * A task to open in PM, as `<projectId>:<taskId>` so it only applies while
   * that project's tab is the one showing (deep links from Blobby).
   */
  pmTask: z.string().optional(),
  /** A Blobby chat to open (the "Change in TeamBlob" link on a Telegram card). */
  blobbyChat: z.string().optional(),
});

export type DesktopSearch = z.infer<typeof desktopSearchSchema>;
export type DesktopSearchKey = keyof DesktopSearch;

/** Narrow an untrusted string to a ModuleKey, or null if it isn't one. */
export function toModuleKey(value: string | undefined): ModuleKey | null {
  if (value === undefined) return null;
  return (MODULE_KEYS as readonly string[]).includes(value)
    ? (value as ModuleKey)
    : null;
}

/**
 * Which search params belong to which module. Add an entry when a module
 * becomes addressable — this table is what lets the shell clear one module's
 * params when another takes focus.
 */
const MODULE_PARAM_KEYS: Partial<
  Record<ModuleKey, ReadonlyArray<DesktopSearchKey>>
> = {
  pm: ["pmTab", "pmTask"],
  crm: ["crmTab"],
  people: ["peopleTab"],
  finance: ["financeTab"],
  inventory: ["inventoryTab"],
  retainers: ["retainersTab", "retainersId"],
  qa: ["qaTab", "qaRun"],
  rewards: ["rewardsTab"],
  settings: ["settingsTab"],
  blobby: ["blobbyChat"],
};

/**
 * Reduce the search params to just the focused window's state.
 *
 * Without this, params from a window you have since navigated away from ride
 * along in the URL — `?win=people&pmTab=my-tasks`, describing a PM tab while a
 * People window is on screen. Worse than untidy: Back then steps through
 * history entries that differ only in a param nothing visible is reading, so
 * the URL changes and the screen does not. Back looks broken.
 *
 * A null `win` (every window closed) clears the lot.
 */
export function scopeSearchToWindow(
  search: DesktopSearch,
  win: ModuleKey | null,
): DesktopSearch {
  const next: DesktopSearch = { ...search };
  for (const [moduleKey, params] of Object.entries(MODULE_PARAM_KEYS)) {
    if (moduleKey === win) continue;
    for (const param of params ?? []) delete next[param];
  }
  next.win = win ?? undefined;
  return next;
}

/**
 * Resolve a module's active tab from the URL.
 *
 * Pure and exported so the fallback rules are testable without a router:
 * an unknown/absent value lands on `fallback`, and `guard` gets the last word
 * for tabs that only exist while a record is open.
 */
export function resolveTab<T extends string>(
  raw: string | undefined,
  tabs: readonly T[],
  fallback: T,
  search: DesktopSearch,
  guard?: (tab: T, search: DesktopSearch) => T,
  /**
   * Accepts tab ids that can't be listed ahead of time — PM's project tabs
   * are `project:<uuid>`, one per open record. Consulted before the fixed
   * list; returning null means "not mine", and narrowing continues.
   */
  dynamic?: (raw: string) => T | null,
): T {
  if (raw !== undefined && dynamic) {
    const accepted = dynamic(raw);
    if (accepted !== null) return guard ? guard(accepted, search) : accepted;
  }
  const narrowed = (tabs as readonly string[]).includes(raw ?? "")
    ? (raw as T)
    : fallback;
  return guard ? guard(narrowed, search) : narrowed;
}

/* ──────────────────────────────────────────────────────────────────
 * URL ⇄ focus sync — the two decisions the window manager's effects make.
 *
 * `?win=` names the focused window. One effect applies the URL to the store
 * (first load, Back/Forward, a pasted or in-app link); the other writes the
 * store to the URL (focus moved by click, dock, or ⌘`). Pushing state in
 * opposite directions is how a feedback loop starts, so both read and update
 * one shared memory.
 *
 * Both effects run in the same pass with the values of the same render. When
 * the URL changes, the URL→store side asks for the new window, but the store
 * has not applied it yet, so the store→URL side still sees the OLD window
 * focused. Treating that as "the user focused the old window" writes the old
 * window back, the store then catches up with the new one, and the two flip
 * each other for ever: the Blobby-link bug (2026-10-12). The rule that stops
 * it: the store→URL side writes only when focus has actually moved since its
 * last pass.
 * ────────────────────────────────────────────────────────────────── */

/** What the sync remembers between renders. Owned by one ref. */
export interface FocusSyncMemory {
  /** The last `win` reconciled from the URL, or last written to it. */
  appliedWin: string | undefined;
  /** Focus as of the store→URL side's last pass. */
  prevFocused: ModuleKey | null;
}

export function createFocusSyncMemory(): FocusSyncMemory {
  return { appliedWin: undefined, prevFocused: null };
}

/**
 * URL → store: the window the URL asks to focus, or null for nothing to do.
 * Acts only on a `win` it has not reconciled yet, so a plain window click,
 * which leaves `win` untouched, is filtered out here.
 */
export function urlFocusTarget(
  memory: FocusSyncMemory,
  win: string | undefined,
  focusedKey: ModuleKey | null,
): ModuleKey | null {
  if (win === memory.appliedWin) return null;
  memory.appliedWin = win;
  const key = toModuleKey(win);
  return key === null || key === focusedKey ? null : key;
}

/**
 * store → URL: whether to rewrite `?win=` (scoped to the focused window), and
 * whether that write replaces the current entry. Null leaves the URL alone.
 */
export function focusUrlWrite(
  memory: FocusSyncMemory,
  win: string | undefined,
  focusedKey: ModuleKey | null,
): { replace: boolean } | null {
  // Nothing is focused yet but the URL names a window: a reload. The URL→store
  // side has asked for that window and the store has not applied it. Writing
  // now would scope the search to `null` and strip every module param, so the
  // reload would land on a bare /dashboard. focusedKey is not persisted, so
  // the URL is the only record of where the user was.
  if (focusedKey === null && toModuleKey(win) !== null) return null;

  // Focus moved because the URL told it to. The address bar is already right;
  // writing again would cost the user a second Back press.
  if (focusedKey === (memory.appliedWin ?? null)) {
    memory.prevFocused = focusedKey;
    return null;
  }

  const focusMoved = focusedKey !== memory.prevFocused;
  // The URL moved, not the focus: the URL→store side is applying it in this
  // same pass. Writing the stale focus back is the loop described above.
  if (!focusMoved && toModuleKey(memory.appliedWin) !== null) return null;

  const hadFocusBefore = memory.prevFocused !== null;
  memory.prevFocused = focusedKey;
  memory.appliedWin = focusedKey ?? undefined;
  return {
    // Push when moving between windows: that is navigation in a desktop, and
    // it lets Back return to the window you came from with its tab intact.
    // Replace on the first write (page load, before any window had focus) so
    // the bootstrapped `?win=dashboard` is not an entry to Back through, and
    // when only correcting a URL that names no window: that is not navigation.
    replace: !hadFocusBefore || !focusMoved,
  };
}

/** Field-wise equality, so we can skip writes that would change nothing. */
const SEARCH_KEYS = Object.keys(
  desktopSearchSchema.shape,
) as DesktopSearchKey[];

export function isSameSearch(a: DesktopSearch, b: DesktopSearch): boolean {
  return SEARCH_KEYS.every((key) => a[key] === b[key]);
}
